import { createContext } from 'react';

import type { CommandPaletteState } from '../state-machine';
import type { ViewCommand } from '../types/command';

/**
 * Internal state context value -- the full reducer state shape.
 *
 * Two read surfaces sit on top of this single context:
 *
 *   - `useInternalCommandPaletteState()` (package-internal) returns
 *     the full shape for components that need to branch on view mode.
 *   - `useCommandPaletteState()` (public) narrows to
 *     `{ isOpen, query, rootCommandId }`, the consumer-relevant slice.
 *
 * The narrow public surface lets us evolve the internal shape without
 * breaking consumers. Both hooks subscribe to the same context, so
 * the actual re-render cost is the destructure shape -- React's
 * context re-renders any consumer when the value identity changes,
 * regardless of which fields they read. Narrowing is a typing
 * boundary, not a perf boundary.
 */
export type CommandPaletteStateContextValue = CommandPaletteState;

/**
 * Internal action surface. Each action emits its analytics event(s)
 * inline (via the registry's `onEvent` callback) and dispatches to
 * the reducer. Identity is stable for the lifetime of the provider,
 * so dispatch-only consumers don't re-render on state changes.
 *
 * Notable shape:
 *
 *   - `openRoot(id)` enters a root by id. Atomic: works from any
 *     prior state (closed, open), opens the palette if needed.
 *     Mirrors `openView(command, prefill)` from the view subsystem.
 *   - `exitRoot()` / `exitView()` take no arguments. They emit a
 *     payload-free analytics event (the palette doesn't allow
 *     nested roots or nested views, so consumers reconstruct
 *     "which one exited" from the preceding entered event in the
 *     sequence). No need to read prev state, no need to pass
 *     entities through call sites.
 *   - `openView` validates `prefill` against the command's
 *     `prefillSchema` (if declared) before dispatching. Schema
 *     errors warn + open with `undefined` prefill rather than
 *     dispatching corrupt state.
 *   - No actions wrap in `startTransition`. We have no Suspense
 *     boundaries; every state change is cheap; snap commit
 *     specifically requires synchronous mode flip.
 */
export interface CommandPaletteActionsContextValue {
  open: (trigger: 'button' | 'keyboard') => void;
  close: () => void;
  setQuery: (query: string) => void;
  openRoot: (id: string, query?: string) => void;
  exitRoot: () => void;
  /**
   * Internal plumbing: deliberately *not* generic over the prefill
   * type. The typed surface is the public `useCommandPaletteActions().
   * openView(id, prefill)`, which keys prefill off {@link
   * ViewCommandRegistry}; by the time we reach this context method the
   * caller already holds a resolved `ViewCommand<unknown>` and an
   * opaque prefill. `openView` re-validates that prefill against the
   * command's `prefillSchema` at runtime before dispatching. Keeping
   * this signature loose lets the public hook call it without casting
   * a command-keyed generic onto an id-keyed one.
   */
  openView: (command: ViewCommand<unknown>, prefill?: unknown) => void;
  exitView: () => void;
}

/**
 * Context for command palette state. Re-renders subscribers on every
 * reducer dispatch.
 */
export const CommandPaletteStateContext = createContext<CommandPaletteStateContextValue | null>(null);

/**
 * Context for command palette actions. Stable identity for the
 * lifetime of the provider; consumers that only dispatch never
 * re-render on state changes.
 */
export const CommandPaletteActionsContext = createContext<CommandPaletteActionsContextValue | null>(null);
